import { DocumentSource, QuoteDocumentConfig } from '@monetize/types/app';
import { FC } from 'react';
import { MdDragIndicator, MdOutlineFileDownload } from 'react-icons/md';
import {
  MBox,
  MCustomIconButton,
  MDataTableActions,
  MFlex,
  MIcon,
  MSpinner,
  MSwitch,
  MTag,
  MText,
  MTooltip,
} from '../../../../../components/Monetize';
import { MCustomSelect } from '../../../../../components/Monetize/MCustomSelect/MCustomSelect';
import { useMDraggable } from '../../../../../components/Monetize/useMDraggable';
import { getRowSourceLabel } from '../quoteDocumentsListUtils';
import {
  ITEM_TYPE,
  QuoteDocumentsMode,
  ROW_GRID_TEMPLATE_COLUMNS,
  SETTINGS_ROW_GRID_TEMPLATE_COLUMNS,
} from './constants';
import { QuoteDocumentNameCell } from './QuoteDocumentNameCell';

interface QuoteDocumentRowSharedProps {
  index: number;
  mode: QuoteDocumentsMode;
  onMove: (dragIndex: number, hoverIndex: number) => void;
  onDropEnd: () => void;
}

interface QuoteDocumentRowDividerProps extends QuoteDocumentRowSharedProps {
  isDivider: true;
  isReadOnly: boolean;
  // Quote-only: settings mode has no "quote" to open/download, so the
  // divider there is a plain, non-interactive boundary marker.
  isOpening?: boolean;
  isDownloading?: boolean;
  onOpenQuote?: () => void;
  onDownloadQuote?: () => void;
}

interface QuoteDocumentRowDocumentProps extends QuoteDocumentRowSharedProps {
  isDivider?: false;
  doc: QuoteDocumentConfig;
  quoteDocumentTypes: Array<{ id: string; label: string; value: string }>;
  isReadOnly: boolean;
  isChangingType: boolean;
  isTogglingAutoAdd?: boolean;
  isOpening: boolean;
  isDownloading: boolean;
  replacingFile: File | null;
  onOpenDocument: (documentConfigId: string, filename?: string) => void;
  onDownloadDocument: (doc: QuoteDocumentConfig) => void;
  onRemoveDocument: (documentConfigId: string) => void;
  onRequestReplace: (doc: QuoteDocumentConfig) => void;
  onChangeType: (doc: QuoteDocumentConfig, documentTypeId: string) => void;
  onToggleAutoAdd?: (doc: QuoteDocumentConfig, checked: boolean) => void;
}

type QuoteDocumentRowProps =
  | QuoteDocumentRowDividerProps
  | QuoteDocumentRowDocumentProps;

// The Quote divider is a valid reorder target (rows can be dropped above/below
// it) but isn't itself draggable — it shares this row's drop/hover mechanics
// via useMDraggable, it just never renders a drag handle for useMDraggable's
// drag half to attach to.
export const QuoteDocumentRow: FC<QuoteDocumentRowProps> = (props) => {
  const { index, isDivider, mode, onMove, onDropEnd } = props;

  const { dragHandleRef, dropContainerRef, handlerId, opacity } = useMDraggable(
    {
      index,
      rowData: isDivider ? null : props.doc,
      moveRow: onMove,
      saveMoveChanges: onDropEnd,
      acceptType: ITEM_TYPE,
    },
  );

  if (isDivider) {
    const {
      isReadOnly,
      isOpening,
      isDownloading,
      onOpenQuote = () => {},
      onDownloadQuote = () => {},
    } = props;

    return (
      <MBox
        ref={dropContainerRef}
        style={{ opacity }}
        data-handler-id={handlerId}
        data-testid="quote-document-row-divider"
        bg="tGray.support"
        borderRadius="md"
        borderWidth="1px"
        borderColor="border.subtle"
        p={0}
        mb={2}
        transition="box-shadow 0.2s"
      >
        <MBox
          display="grid"
          gridTemplateColumns={
            mode === QuoteDocumentsMode.SETTINGS
              ? SETTINGS_ROW_GRID_TEMPLATE_COLUMNS
              : ROW_GRID_TEMPLATE_COLUMNS
          }
          gap={3}
          alignItems="center"
          px={3}
          py={2}
          minH="56px"
        >
          <MBox />

          <QuoteDocumentNameCell
            fileName="Quote"
            isReplacing={false}
            isLoading={isOpening}
            docId="divider"
            isInteractive={mode === QuoteDocumentsMode.QUOTE}
            onClick={onOpenQuote}
          />

          <MBox minW={0}>
            <MText
              maxW="100%"
              h="auto"
              whiteSpace="normal"
              wordBreak="break-word"
            >
              Order Form
            </MText>
          </MBox>

          {mode === QuoteDocumentsMode.QUOTE ? (
            <>
              <MBox minW={0}>
                <MTag size="sm">System</MTag>
              </MBox>

              <MFlex
                alignItems="center"
                justifySelf="end"
                gap={1}
                flexShrink={0}
              >
                <MTooltip label="Download" placement="top">
                  <MBox as="span" display="inline-flex">
                    <MCustomIconButton
                      isLoading={isDownloading}
                      icon={MdOutlineFileDownload}
                      aria-label="Download quote"
                      data-testid="quote-document-download-btn-divider"
                      btnSize={5}
                      containerSize={6}
                      onClick={onDownloadQuote}
                    />
                  </MBox>
                </MTooltip>

                {!isReadOnly && (
                  <MDataTableActions actions={[]} showPlaceholderIfNoItems />
                )}
              </MFlex>
            </>
          ) : null}
        </MBox>
      </MBox>
    );
  }

  const {
    doc,
    quoteDocumentTypes,
    isReadOnly,
    isChangingType,
    isTogglingAutoAdd,
    isOpening,
    isDownloading,
    replacingFile,
    onOpenDocument,
    onDownloadDocument,
    onRemoveDocument,
    onRequestReplace,
    onChangeType,
    onToggleAutoAdd,
  } = props;

  // Settings has no "quote" to import from, so every doc it manages is
  // library-sourced (never originalSource === QUOTE) — Type must stay
  // editable there regardless of source.
  const isEditableType =
    mode === QuoteDocumentsMode.SETTINGS ||
    doc.originalSource === DocumentSource.QUOTE;
  const isReplacing = !!replacingFile;
  const fileName = replacingFile
    ? replacingFile.name
    : doc.storageObject?.filename || '-';
  const mediaType = replacingFile
    ? replacingFile.type
    : doc.storageObject?.mediaType;
  const gridTemplateColumns =
    mode === QuoteDocumentsMode.SETTINGS
      ? SETTINGS_ROW_GRID_TEMPLATE_COLUMNS
      : ROW_GRID_TEMPLATE_COLUMNS;

  return (
    <MBox
      ref={dropContainerRef}
      style={{ opacity }}
      data-handler-id={handlerId}
      data-testid={`quote-document-row-${doc.id}`}
      bg="white"
      borderRadius="md"
      borderWidth="1px"
      borderColor="border.subtle"
      p={0}
      mb={2}
      transition="box-shadow 0.2s"
    >
      <MBox
        display="grid"
        gridTemplateColumns={gridTemplateColumns}
        gap={3}
        alignItems="center"
        px={3}
        py={2}
        minH="56px"
      >
        <MBox data-testid={`quote-document-drag-handle-${doc.id}`}>
          {!isReadOnly && !isReplacing && (
            <MFlex ref={dragHandleRef} cursor="move" alignItems="center">
              <MIcon as={MdDragIndicator} boxSize={5} color="tPurple.dark" />
            </MFlex>
          )}
        </MBox>

        <QuoteDocumentNameCell
          mediaType={mediaType}
          fileName={fileName}
          isReplacing={isReplacing}
          isLoading={isOpening}
          docId={doc.id}
          onClick={() =>
            onOpenDocument(doc.id, doc.storageObject?.filename ?? undefined)
          }
        />

        <MFlex
          alignItems="center"
          gap={2}
          minW={0}
          data-testid={`quote-document-type-${doc.id}`}
        >
          {isEditableType && !isReadOnly ? (
            <MBox minW={0} w="100%">
              <MCustomSelect
                name={`quote-document-type-${doc.id}`}
                inputTestId={`quote-document-type-select-${doc.id}`}
                items={quoteDocumentTypes}
                itemTitle="label"
                itemValue="id"
                placeholder="Type…"
                value={doc.documentTypeId}
                isDisabled={isChangingType || isReplacing}
                usePortal
                onChange={(documentTypeId: string) =>
                  onChangeType(doc, documentTypeId)
                }
              />
            </MBox>
          ) : (
            <MBox minW={0} maxW="100%">
              <MText
                maxW="100%"
                h="auto"
                whiteSpace="normal"
                wordBreak="break-word"
              >
                {doc.quoteDocumentType.label}
              </MText>
            </MBox>
          )}
        </MFlex>

        {mode === QuoteDocumentsMode.QUOTE && (
          <MBox minW={0} data-testid={`quote-document-source-${doc.id}`}>
            <MTag size="sm">{getRowSourceLabel(doc.originalSource)}</MTag>
          </MBox>
        )}

        {mode === QuoteDocumentsMode.SETTINGS && (
          <MBox
            display="flex"
            justifyContent="center"
            data-testid={`quote-document-auto-add-${doc.id}`}
          >
            <MTooltip
              label="Automatically add document on newly created quotes"
              placement="top"
            >
              <MBox as="span" display="inline-flex">
                <MSwitch
                  size="md"
                  isChecked={doc.autoAddToQuotes}
                  isDisabled={isTogglingAutoAdd || isReplacing || isReadOnly}
                  aria-label={`Automatically add ${fileName} on newly created quotes`}
                  onChange={(event) =>
                    onToggleAutoAdd?.(doc, event.target.checked)
                  }
                />
              </MBox>
            </MTooltip>
          </MBox>
        )}

        <MFlex
          alignItems="center"
          justifySelf="end"
          gap={1}
          flexShrink={0}
          data-testid={`quote-document-actions-${doc.id}`}
        >
          {isReplacing ? (
            <MSpinner size="sm" />
          ) : (
            <>
              {mode === QuoteDocumentsMode.QUOTE && (
                <MTooltip label="Download" placement="top">
                  <MBox as="span" display="inline-flex">
                    <MCustomIconButton
                      isLoading={isDownloading}
                      icon={MdOutlineFileDownload}
                      aria-label={`Download ${fileName}`}
                      data-testid={`quote-document-download-btn-${doc.id}`}
                      btnSize={5}
                      containerSize={6}
                      onClick={() => onDownloadDocument(doc)}
                    />
                  </MBox>
                </MTooltip>
              )}

              {!isReadOnly && (
                <MDataTableActions
                  actions={[
                    {
                      title: 'Replace',
                      enabled: true,
                      action: async () => onRequestReplace(doc),
                    },
                    {
                      title: 'Remove',
                      enabled: true,
                      color: 'danger',
                      confirmProps: {
                        title: `Are you sure you want to remove "${fileName}"?`,
                        yesButton: 'Confirm',
                      },
                      action: async () => onRemoveDocument(doc.id),
                    },
                  ]}
                />
              )}
            </>
          )}
        </MFlex>
      </MBox>
    </MBox>
  );
};
